<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <div class="add-field-modal flex flex-col bg-white rounded-xl">
      <!-- Modal Header -->
      <woot-modal-header-v2
        :header-title="
          isEditMode
            ? $t('CUSTOM_FIELD_MODAL.EDIT_TITLE')
            : $t('CUSTOM_FIELD_MODAL.ADD_TITLE')
        "
        :header-content="
          isEditMode
            ? $t('CUSTOM_FIELD_MODAL.EDIT_DESCRIPTION')
            : $t('CUSTOM_FIELD_MODAL.ADD_DESCRIPTION')
        "
      >
        <template #titleIcon>
          <span
            class="material-symbols-outlined text-[20px] text-gray-800 shrink-0"
          >
            {{ isEditMode ? 'edit' : 'add' }}
          </span>
        </template>
      </woot-modal-header-v2>

      <!-- Content -->
      <div class="px-6 py-4">
        <div class="flex flex-col gap-3">
          <template v-if="!isEditMode">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm !text-gray-800 font-normal">
                {{ $t('CUSTOM_FIELD_MODAL.APPLIES_TO.LABEL') }}
              </label>
              <woot-multiselect
                v-model="selectedTypeOption"
                :options="attributeTypes"
                track-by="value"
                label="label"
                :show-labels="false"
                :allow-empty="false"
                :searchable="false"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm !text-gray-800 font-normal">
                {{ $t('CUSTOM_FIELD_MODAL.CUSTOM_FIELD.LABEL') }}
              </label>
              <woot-multiselect
                v-model="selectedAttribute"
                :options="availableAttributes"
                track-by="attribute_key"
                label="attribute_display_name"
                :show-labels="false"
                :allow-empty="false"
                :searchable="false"
                :max-height="216"
                :placeholder="$t('CUSTOM_FIELD_MODAL.CUSTOM_FIELD.PLACEHOLDER')"
              />
              <p class="text-sm !text-gray-800 leading-5 !mt-0">
                {{ $t('CUSTOM_FIELD_MODAL.CUSTOM_FIELD.HINT') }}
                <a
                  href="#"
                  class="font-semibold text-green-500 hover:text-green-600 transition-colors"
                  @click.prevent="navigateToSettings"
                >
                  {{ $t('CUSTOM_FIELD_MODAL.CUSTOM_FIELD.CREATE_LINK') }}
                </a>
              </p>
            </div>
          </template>

          <template v-else>
            <div v-if="selectedAttribute" class="flex flex-col gap-1.5">
              <label class="text-sm !text-gray-800 font-normal">
                {{ $t('CUSTOM_FIELD_MODAL.APPLIES_TO.LABEL') }}
              </label>
              <div
                class="w-full h-11 border border-gray-200 rounded-lg px-3 py-2 text-base bg-gray-50 text-gray-500 flex items-center"
              >
                {{ selectedTypeOption && selectedTypeOption.label }}
              </div>
            </div>

            <div v-if="selectedAttribute" class="flex flex-col gap-1.5">
              <label class="text-sm !text-gray-800 font-normal">
                {{ $t('CUSTOM_FIELD_MODAL.CUSTOM_FIELD.LABEL') }}
              </label>
              <div
                class="w-full h-11 border border-gray-200 rounded-lg px-3 py-2 text-base bg-gray-50 text-gray-500 flex items-center"
              >
                {{ selectedAttribute.attribute_display_name }}
              </div>
            </div>
          </template>

          <div v-if="selectedAttribute" class="flex flex-col gap-1.5">
            <label
              class="text-sm !text-gray-800 font-normal"
              :class="{
                '!text-[#B91C1C]': shouldShowValueError,
              }"
            >
              {{ $t('CUSTOM_FIELD_MODAL.VALUE.LABEL') }}
            </label>

            <!-- list type -->
            <woot-multiselect
              v-if="isAttributeTypeList"
              v-model="attributeListValue"
              :options="attributeListOptions"
              track-by="id"
              label="name"
              :show-labels="false"
              :allow-empty="false"
              :searchable="false"
              :placeholder="$t('CUSTOM_FIELD_MODAL.CUSTOM_FIELD.PLACEHOLDER')"
            />

            <!-- checkbox type -->
            <label
              v-else-if="isAttributeTypeCheckbox"
              class="flex items-center gap-2 cursor-pointer"
            >
              <input
                v-model="attributeValue"
                type="checkbox"
                class="my-0 !mr-0 semantic-checkbox"
              />
              <span class="text-sm text-gray-700">
                {{ selectedAttribute.attribute_description }}
              </span>
            </label>

            <!-- date type -->
            <date-picker
              v-else-if="isAttributeTypeDate"
              v-model="attributeValue"
              format="DD/MM/YYYY"
              value-type="YYYY-MM-DD"
              class="w-full date-picker"
            >
              <template #icon-calendar>
                <span class="material-symbols-outlined">calendar_month</span>
              </template>
            </date-picker>

            <!-- text, number, link types -->
            <template v-else>
              <input
                v-model="attributeValue"
                :type="attributeInputType"
                class="w-full h-11 border rounded-lg px-3 py-2 text-base bg-white text-gray-700 focus:outline-none focus:border-gray-400 hover:border-gray-400 transition-colors !mb-0"
                :class="{
                  'border-[#B91C1C]': shouldShowValueError,
                  'border-gray-300': !shouldShowValueError,
                }"
                @blur="$v.attributeValue.$touch"
              />
              <span v-if="shouldShowValueError" class="text-[#B91C1C] text-sm">
                {{ valueErrorMessage }}
              </span>
            </template>
          </div>
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-3 border-t border-gray-100 px-6 pt-4 pb-6"
        style="box-shadow: 0px -1px 2px rgba(202, 202, 202, 0.16)"
      >
        <woot-button
          class="button clear text-base px-5 py-3 leading-[20px] h-[48px] font-[400]"
          color-scheme="secondary"
          @click.prevent="onClose"
        >
          {{ $t('CUSTOM_FIELD_MODAL.BUTTONS.CANCEL') }}
        </woot-button>
        <woot-submit-button
          :disabled="isSubmitDisabled"
          :button-text="
            isEditMode
              ? $t('CUSTOM_FIELD_MODAL.BUTTONS.SAVE')
              : $t('CUSTOM_FIELD_MODAL.BUTTONS.ADD')
          "
          class="button-filled text-base px-5 py-3 font-[400] h-[48px]"
          @click.native.prevent="onSubmit"
        />
      </div>
    </div>
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, url } from 'vuelidate/lib/validators';
import DatePicker from 'vue2-datepicker';

export default {
  components: { DatePicker },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'add', // 'add' | 'edit'
    },
    initialAttributeType: {
      type: String,
      default: 'conversation_attribute',
    },
    editAttribute: {
      type: Object,
      default: null,
    },
    contactId: {
      type: Number,
      default: null,
    },
    ticketId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      selectedTypeOption: null,
      selectedAttribute: null,
      attributeValue: null,
      attributeListValue: null,
      attributeTypes: [
        {
          value: 'conversation_attribute',
          label: this.$t('CUSTOM_FIELD_MODAL.APPLIES_TO.CONVERSATION'),
        },
        {
          value: 'contact_attribute',
          label: this.$t('CUSTOM_FIELD_MODAL.APPLIES_TO.CONTACT'),
        },
        {
          value: 'ticket_attribute',
          label: this.$t('CUSTOM_FIELD_MODAL.APPLIES_TO.TICKET'),
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentChat: 'getSelectedChat',
    }),
    isEditMode() {
      return this.mode === 'edit';
    },
    selectedType() {
      return this.selectedTypeOption
        ? this.selectedTypeOption.value
        : this.initialAttributeType;
    },
    allAttributes() {
      return this.$store.getters['attributes/getAttributesByModel'](
        this.selectedType
      );
    },
    currentCustomAttributes() {
      if (this.selectedType === 'conversation_attribute') {
        return this.currentChat.custom_attributes || {};
      }
      if (this.selectedType === 'ticket_attribute') {
        const ticket = this.$store.getters['tickets/getCurrentTicket'];
        return (ticket && ticket.custom_attributes) || {};
      }
      const contact = this.$store.getters['contacts/getContact'](
        this.contactId
      );
      return (contact && contact.custom_attributes) || {};
    },
    availableAttributes() {
      return this.allAttributes.filter(
        attr =>
          !Object.keys(this.currentCustomAttributes).includes(
            attr.attribute_key
          )
      );
    },
    isAttributeTypeList() {
      return this.selectedAttribute?.attribute_display_type === 'list';
    },
    isAttributeTypeCheckbox() {
      return this.selectedAttribute?.attribute_display_type === 'checkbox';
    },
    isAttributeTypeDate() {
      return this.selectedAttribute?.attribute_display_type === 'date';
    },
    attributeInputType() {
      const type = this.selectedAttribute?.attribute_display_type;
      return type === 'link' ? 'url' : type || 'text';
    },
    attributeListOptions() {
      return (this.selectedAttribute?.attribute_values || []).map(
        (value, index) => ({ id: index + 1, name: value })
      );
    },
    isSubmitDisabled() {
      if (!this.selectedAttribute) return true;
      if (this.isEditMode) return false;
      return !this.attributeValue && !this.attributeListValue;
    },
    shouldShowValueError() {
      return this.$v.attributeValue.$error;
    },
    valueErrorMessage() {
      if (!this.$v.attributeValue.url) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
  },
  watch: {
    selectedTypeOption() {
      if (this.isEditMode) return;
      this.selectedAttribute = null;
      this.attributeValue = null;
      this.attributeListValue = null;
    },
    selectedAttribute() {
      if (this.isEditMode) return;
      this.attributeValue = null;
      this.attributeListValue = null;
    },
    show(val) {
      if (val) {
        if (this.isEditMode && this.editAttribute) {
          this.selectedAttribute = this.editAttribute;
          this.prefillValueFromAttribute(this.editAttribute);
          this.selectedTypeOption = this.attributeTypes.find(
            t => t.value === this.selectedAttribute.attribute_model
          );
        } else {
          this.selectedTypeOption = this.attributeTypes.find(
            t => t.value === this.initialAttributeType
          );
          this.selectedAttribute = null;
          this.attributeValue = null;
          this.attributeListValue = null;
        }
      }
    },
    attributeTypes: {
      immediate: true,
      handler(types) {
        if (!this.selectedTypeOption && !this.isEditMode) {
          this.selectedTypeOption =
            types.find(t => t.value === this.initialAttributeType) || types[0];
        }
      },
    },
  },
  validations() {
    if (
      !this.selectedAttribute ||
      this.isAttributeTypeList ||
      this.isAttributeTypeCheckbox ||
      this.isAttributeTypeDate
    ) {
      return { attributeValue: {} };
    }
    if (this.isAttributeTypeLink) {
      return { attributeValue: { required, url } };
    }
    return { attributeValue: { required } };
  },
  methods: {
    prefillValueFromAttribute(attr) {
      if (attr.attribute_display_type === 'list') {
        const options = (attr.attribute_values || []).map((v, i) => ({
          id: i + 1,
          name: v,
        }));
        this.attributeListValue =
          options.find(o => o.name === attr.value) || null;
        this.attributeValue = null;
      } else {
        this.attributeValue = attr.value ?? null;
        this.attributeListValue = null;
      }
    },
    onClose() {
      this.$emit('close');
    },
    onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      if (!this.selectedAttribute) return;
      const value = this.isAttributeTypeList
        ? this.attributeListValue?.name ?? null
        : this.attributeValue;

      if (this.isEditMode) {
        this.$emit('update-field', {
          attributeKey: this.selectedAttribute.attribute_key,
          value,
        });
      } else {
        this.$emit('add-field', {
          attributeType: this.selectedType,
          attribute: {
            ...this.selectedAttribute,
            default_value: value,
          },
        });
      }
      this.onClose();
    },
    navigateToSettings() {
      this.$router.push(
        `/app/accounts/${this.accountId}/settings/custom-attributes/list`
      );
      this.onClose();
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .modal-container {
    @apply rounded-[12px];
    overflow: visible !important;
  }

  .multiselect {
    margin-bottom: 0;
  }

  .mx-datepicker {
    width: 100%;

    .mx-input {
      @apply h-11 border border-gray-300 rounded-lg px-3 text-base bg-white text-gray-700 mb-0;
      box-shadow: none;

      &:hover {
        @apply border-gray-400;
      }

      &:focus {
        @apply border-gray-400;
        outline: none;
        box-shadow: none;
      }
    }

    .mx-icon-calendar {
      @apply text-gray-500;
    }
  }
}
</style>
<style>
.mx-datepicker-main.mx-datepicker-popup {
  z-index: 9999;
}
</style>
